import { Injectable } from '@angular/core';
import { SharedConstants, User } from '@formbird/types';
import { isEqual } from 'lodash';
import { Observable } from 'rxjs';
import { DocumentService } from './document.service';
import { ClientAccessService } from '../access/client-access.service';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { UnsavedDocumentService } from './unsaved-document.service';
import { BroadcastService } from '../broadcast/broadcast.service';
import { ClientConstants } from '../../constants/ClientConstants';
import { select } from '../../redux/decorators/select';

const logger = console;

@Injectable({
  providedIn: 'root'
})
export class ModifiedFieldService {

  @select(['userState', 'user']) user$: Observable<User>;
  user: User;

  // list of modified fields in a document
  public modifiedFields = {};

  // controls the dirty checking
  public formDirty = {
    showSpinner: false,
    isSaving: false,
    dirty: false,
    updateAllowed: false,
    validating: null
  };

  constructor(
    private documentService: DocumentService,
    private clientAccessService: ClientAccessService,
    private loggedInUserService: LoggedInUserService,
    private unsavedDocumentService: UnsavedDocumentService,
    private broadcastService: BroadcastService
  ) {
    this.user$.subscribe((user: User) => {
      this.user = user;
    });
  }

  private canUpdateDoc(targetDocument) {

    if (!targetDocument) {
      return false;
    }

    // if the document is new
    if (targetDocument.systemHeader && !targetDocument.systemHeader.createdDate) {
      // check if user can create
      return this.clientAccessService.checkAccess(targetDocument, SharedConstants.OPERATION_TYPE_CREATE);

    } else {

      // check if user can update
      return this.clientAccessService.checkAccess(targetDocument, SharedConstants.OPERATION_TYPE_UPDATE);
    }
  }

  /**
   * Check if the current form is dirty, that make the save button appear and blink or display unsaved changes dialog
   */
  isDirty() {
    return this.formDirty.dirty;
  }

  /**
   * Set current form to be dirty, that make the save button appear and blink
   * @param isDirty is dirty
   * @param documentId the document id
   */
  setDirty(isDirty, documentId?) {

    if (isDirty !== this.formDirty.dirty) {

      // don't set the dirty value if save is in progress. Any change in a presave server rule will go into the save. The
      // isSaving flag should have been cleared by the time of a postSave rule so that the save button will appear if the
      // document is changed in the postSave rule
      if (isDirty) {

        if (!this.formDirty.isSaving) {

          if (this.formDirty.updateAllowed) {

            this.formDirty.dirty = isDirty;

          } else {

            const targetDoc = this.documentService.getExistingDocument(documentId);

            if (this.canUpdateDoc(targetDoc)) {

              this.formDirty.dirty = isDirty;
            }
          }
        }

      } else {

        this.formDirty.dirty = false;

      }
    }

    this.broadcastService.broadcast(ClientConstants.FORM_DIRTY_UPDATED, this.formDirty);
  }

  setShowSpinner(showSpinner) {

    this.formDirty.showSpinner = showSpinner;

    this.broadcastService.broadcast(ClientConstants.SPINNER_UPDATED, showSpinner);

  }

  /**
   * Allow this form to be updatable.
   * @param isAllowedUpdate is allowed update
   */
  setUpdateAllowed(isAllowedUpdate) {

    this.formDirty.updateAllowed = isAllowedUpdate;
  }

  /**
   * Reset the current form to its un-interacted state.
   */
  resetFormDirty() {
    this.setDirty(false, null);
  }

  /**
   * Sets the current form to be updatable depending on the current user's privileges.
   * @param docData the doc data object
   */
  setFormAccess(docData) {

    this.setUpdateAllowed(false); // default to false

    const userAccount = this.user ? this.user.account : null;

    // do not allow update
    if (!docData || !userAccount) {
      return;
    }

    if (this.canUpdateDoc(docData)) {
      this.setUpdateAllowed(true);
    }
  }

  isSaving() {
    return this.formDirty.isSaving;
  }

  setSaveFlag(isSaving) {
    this.formDirty.isSaving = isSaving;
  }

  /*
   * This method is used to add modified field by document id.
   * Before adding should check whether the version of the document has changed from the original because
   * there is a case reset current document from the one returned from server once it is saved.
   * @returns: true if adding successfully, otherwise: false
   */
  addModifiedField(documentId, changedFieldName, documentListId, shouldCompareDocuments?, overrideTemplateId?) {

    if (this.formDirty.isSaving) {
      logger.info('Document is saving. Ignore adding modified field.');
      return;
    }

    let result = false;

    if (!documentListId) {
      logger.error('Document list id not provided in addModifiedField');

    } else if (!documentId) {
      logger.error('Document id not provided in addModifiedField');

    } else if (!changedFieldName) {
      logger.error('Changed field name not provided in addModifiedField');

    } else if (!this.unsavedDocumentService.isDocumentsDefined(documentListId)) {
      logger.info('Unsaved Document List is not found');

    } else {

      let shouldAdd = false;
      if (shouldCompareDocuments === undefined || shouldCompareDocuments === true) {

        shouldAdd = this.isChangedField(documentId, changedFieldName, documentListId, overrideTemplateId);

      } else {

        shouldAdd = true;
      }

      if (shouldAdd) {

        if (!this.modifiedFields[documentId]) {
          this.modifiedFields[documentId] = {};
        }

        this.modifiedFields[documentId][changedFieldName] = true;
        this.modifiedFields[documentId][ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY] = documentListId;

        this.setDirty(true, documentId);

        logger.info('Adding new modified field. Doc: ' + documentId + ' - field: ' + changedFieldName);
        logger.info('Stored modified fields: ' + JSON.stringify(this.modifiedFields));

        result = true;
      }
    }

    return result;
  }

  resetModifiedFields() {

    logger.info('Resetting all modified fields...');

    this.modifiedFields = {};
  }

  // Remove all changed fields of a document
  removeModifiedDocumentFields(documentId) {

    logger.info('Resetting all modified fields of a document...');

    delete this.modifiedFields[documentId];
  }

  // Remove all changed fields of a template
  removeTemplateFields(templateId) {

    logger.info('Removing changed fields of template: ' + templateId);

    delete this.modifiedFields[templateId];
  }

  removeChangedField(documentId, changedFieldName) {
    logger.warn('DEPRECATED: Please call ModifiedFieldService.removeModifiedField ' +
      'instead of ModifiedFieldService.removeChangedField');

    this.removeModifiedField(documentId, changedFieldName);
  }

  removeModifiedField(documentId, changedFieldName) {

    if (this.modifiedFields[documentId]) {

      logger.info('Removing changed field: ' + changedFieldName + ' of document: ' + documentId);

      delete this.modifiedFields[documentId][changedFieldName];
    }

    if (!this.hasAnyChanged()) {
      this.setDirty(false, null);
    }
  }

  isChangedField(documentId, changedFieldName, documentListId, overrideTemplateId?) {

    try {

      const currentDoc = this.unsavedDocumentService.getDocumentOnly(documentId);
      const currentOriginalDoc = this.documentService.getExistingDocument(documentId);

      if (currentDoc && !currentOriginalDoc ||    // create doc mode
        (currentDoc && currentOriginalDoc &&    // modify doc && component editor mode
          !isEqual(currentDoc[changedFieldName], currentOriginalDoc[changedFieldName])) ||
        (overrideTemplateId && !isEqual(currentDoc, currentOriginalDoc))) {   // override template

        return true;
      }

    } catch (e) {
    }

    return false;
  }

  /**
   * whether the document has changed
   * @param documentId the document id
   * @returns the result
   */
  hasChanged(documentId) {

    logger.info('Checking changed document. Stored modified fields: ' + JSON.stringify(this.modifiedFields));

    const changes = this.getModifiedFieldsInDoc(documentId);

    return changes.length > 0;
  }

  /**
   * whether any documents have been changed
   * @returns the result
   */
  hasAnyChanged() {

    const documentIds = Object.keys(this.modifiedFields);

    if (documentIds) {

      for (let id = 0; id < documentIds.length; id++) {

        const infos = Object.keys(this.modifiedFields[documentIds[id]]);

        for (let i = 0; i < infos.length; i++) {

          const key = infos[i];
          if (key !== ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY) {

            return true;
          }

        }
      }
    }

    return false;
  }

  /**
   * get the unsaved documents for the unsaved document list. This needs to be separate from UnsavedDocumentService to avoid
   * a circular dependency of Angular service injections
   * @param unsavedDocumentListId - the id of the list to get the changed docs for
   */
  getModifiedDocuments(unsavedDocumentListId) {
    if (!unsavedDocumentListId) {
      throw new Error('Unsaved document list id not specified when getting loaded documents');
    }

    const self = this;

    const loadedDocs = this.unsavedDocumentService.getUnsavedDocuments(unsavedDocumentListId);

    // only return documents that have unsaved changes. Load the modified docs into unsavedDocs
    const unsavedDocs = [];

    if (loadedDocs) {


      loadedDocs.forEach(function (loadedDoc) {

        if (self.hasChanged(loadedDoc.documentId)) {

          unsavedDocs.push(loadedDoc);

        }

      });
    }

    return unsavedDocs;
  }

  getChangedDocumentListId(documentId) {

    return this.modifiedFields[documentId] ?
      this.modifiedFields[documentId][ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY] : null;
  }

  getAllChangedDocumentListIds() {
    const tmp = [];
    const keys = Object.keys(this.modifiedFields);

    for (let i = 0; i < keys.length; i++) {

      if (this.modifiedFields[keys[i]][ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY]) {

        tmp.push(this.modifiedFields[keys[i]][ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY]);
      }
    }

    return tmp;
  }

  getModifiedFieldsInDoc(documentId) {
    const changedFieldNames = [];

    const info = this.modifiedFields[documentId];

    if (info) {

      const keys = Object.keys(info);

      for (let i = 0; i < keys.length; i++) {

        const key = keys[i];

        if (key !== ClientConstants.CHANGED_DOCUMENT_LIST_ID_KEY) {

          changedFieldNames.push(key);
        }
      }
    }

    return changedFieldNames;
  }

}
